/**
 * @flow
 *
 * This file handles validation of our form fields
 */
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import isNumeric from 'validator/lib/isNumeric';
import isDecimal from 'validator/lib/isDecimal';
import moment from 'moment-timezone';
import { MetricToImperialConverter } from 'fitbeat.models';

/**
 * Checks if an email is valid:
 * 1) It cannot be empty
 * 2) It must be a valid email format
 */
export const isEmailValid = (email: string): ?string => (!!email && isEmail(email) ? undefined : 'Invalid email');

/**
 * Checks if a name is valid:
 * 1) It cannot be empty
 */
export const isNameValidNotRequired = (name) => {
    if (!!name && !isEmpty(name)) {
        return maxLength(20)(name);
    } else {
        return  undefined;
    }
}

export const isNameValid = (name) => {
    if (!!name && !isEmpty(name)) {
        return maxLength(20)(name);
    } else {
        return 'Required';
    }
}

export const isLiveStreamTopicValid = (topic) => {
  if (!!topic) {
    return maxLength(30)(topic);
  }
};

export const isLiveStreamCategoryValid = (category) => {
  if (!!category && !isEmpty(category)) {
    return maxLength(10)(category);
  }
};

/**
 * Checks if a password is valid:
 * 1) It cannot be empty
 */
// TODO: Proper password validation
export const isPasswordValid = (password: string): ?string => (!!password && !isEmpty(password) ? undefined : 'Required');

/**
 * Checks if a phone number is valid:
 * 1) It cannot be empty
 */
const mobileRegexCountryCode = /^\+[0-9]{7,17}$/;
const mobileRegexWithoutCountryCode = /^\+?[0-9]{7,15}$/;
export const isPhoneNumberValid = (phoneNumber) => (
    !phoneNumber ||
    mobileRegexWithoutCountryCode.test(phoneNumber) ||
    mobileRegexCountryCode.test(phoneNumber) ? undefined : 'Invalid');

export const isPhoneNumberValidNoCountryCode = (phoneNumber) => {
    if (!phoneNumber || !mobileRegexWithoutCountryCode.test(phoneNumber)) {
        return 'Invalid'
    }
    return undefined;
}

/**
 * Checks if a number is valid:
 * 1) If it is a number/decimal
 * 2) OPTIONAL: can be empty
 */

export const isNumber = (number = '') => {
    const num = number.replace(',' , '.');
    return ((isDecimal(num) || isNumeric(num)
    || isEmpty(num)) ? undefined : 'Invalid Number');
};
/**
 * Checks if field is not empty:
 * 1) It cannot be empty
 */
export const isRequired = (value: string): ?string => (!!value && !isEmpty(value) ? undefined : 'Required');

/**
 * Checks if field is same as value:
 * 1) It must be the same value
 */
export const isPasswordSame = (value: string, allValues): ?string => (value === allValues.password ? undefined : 'Passwords do not match');

export const isEmailSame = (value: string, allValues): ?string => (value === allValues.email ? undefined : 'Emails do not match');

export const maxLength = max => value =>
    value && value.length > max ? `Must be ${max} characters or less` : undefined

export const minLength = min => value =>
    value && value.length < min ? `Must be ${min} characters or more` : undefined

const minValue = min => value =>
    value && value < min ? `Must be at least ${min}` : undefined;

const maxValue = min => value =>
    value && value > min ? `Must be at most ${min}` : undefined;

export const validValue = value => undefined;

export const minLength8 = minLength(8);
export const minLength6 = minLength(6);
export const minLength3 = minLength(3);

export const minWeight = minValue(20);
export const maxWeight = maxValue(300);
export const minWeightImperial= minValue(44)
export const maxWeightImperial= maxValue(661)

export const minHeight = minValue(120);
export const maxHeight = maxValue(305);
export const minHeightImperial= minValue(4)
export const maxHeightImperial= maxValue(10)
export const heightChecksImperial =  (value) => {
    if (value && typeof JSON.parse(value) === 'object') {
        const parsedHeightObject = JSON.parse(value);
        const inches = parsedHeightObject.inches ? parsedHeightObject.inches : 0;
        const feet = MetricToImperialConverter.convertInchesAndFeetToFeet(parsedHeightObject);
        if ((parsedHeightObject.inches && !!isNumber(parsedHeightObject.inches)) ||
            (parsedHeightObject.feet && !!isNumber(parsedHeightObject.feet)) ) {
            return 'Invalid Number';
        }
        if (feet === 0 ) {
            return 'Required';
        }
        if (inches > 12) {
            return 'Inches must be at most 12';
        }
        if (feet > 10) {
            return 'Height must be at most 10\' 0\"';
        }
        if (feet < 4) {
            return 'Height must be at least 4\' 0\"';
        }
     }
};

export const isValidDate = (date = '') => (moment(date, 'DD-MM-YYYY', true).isValid() || moment(date, 'D MMMM YYYY', true).isValid() ? undefined : 'Required');

export const isDateOlderThan16 = (date = '') => {
    const formatedDate = (moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD'));
    const yearsAgo = moment().subtract(16, 'years').format('YYYY-MM-DD');
    return moment(formatedDate).isSameOrBefore(yearsAgo) ? undefined : 'Must be 16 years or older';
};

export const uniqueEmergencyContact = (value, allFields) => {
    return value == allFields.mobileNumber ? 'Not unique' : undefined;
};

export const isNumberOrEmpty = (value) => (value === '' || isNumber(value) === undefined ? undefined : 'Invalid number');

export const isNumberRequired = (value) => (!!value ? undefined : 'Required');
